import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PersonalConstants } from 'app/common/personalConstants';
import { FormErrorMapperService } from 'app/shared/services/form-error-mapper.service';
import { LocationService } from 'app/shared/services/location.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { catchError, map, Observable, of, Subject, Subscription, switchMap } from 'rxjs';

export interface vehicleLocationAddress {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    locationDescription: string;
}

@Component({
  selector: 'is-vehicle-location-address-form',
  standalone: false,
  templateUrl: './vehicle-location-address-form.component.html',
  styleUrl: './vehicle-location-address-form.component.scss'
})
export class VehicleLocationAddressFormComponent implements OnInit {
    @Output() formValid = new EventEmitter<UntypedFormGroup>();

    constructor (
        private formBuilder: UntypedFormBuilder,
        public formError: FormErrorMapperService,
        private locationService: LocationService,
        private logger: LoggerService
    ) { }
    vehicleLocationForm: UntypedFormGroup;
    private subscriber: Subscription;
    private readonly UNKNOWN_ADDRESS_TEXT = 'I dont know';
    countryOptions = [
        { value: 'US', name: 'United States' },
        { value: 'CA', name: 'Canada' },
        { value: 'MX', name: 'Mexico' }
    ];

    stateOptions: { value: string; name: string }[] = [];
    addressLineSuggestions: string[] = [];
    suggestedParsedAddressess: { addressLine1: string; city: string; state: string; zipCode: string; country: string; pbkey?: string; locationDescription?: string }[] = [];
    addressKeys: string[] = [];
    stateLabel = 'State';
    zipCodeLabel = 'Zip Code';

    private searchTerm: Subject<string> = new Subject<string>();

    private statesByCountry = {
        US: PersonalConstants.states,
        CA: PersonalConstants.canadaStates,
        MX: PersonalConstants.mexicoStates
    };

    maxDate = new Date();

    ngOnDestroy(): void {
        this.subscriber.unsubscribe();
    }

    ngOnInit(): void {
        this.vehicleLocationForm = this.formBuilder.group({
            country: ['', [Validators.required]],
            state: ['', [Validators.required]],
            addressLine1: ['', Validators.compose([Validators.required, Validators.pattern(PersonalConstants.regexValidators.newAddressField)])],
            addressLine2: ['', Validators.compose([Validators.pattern(PersonalConstants.regexValidators.newAddressField)])],
            city: ['', Validators.compose([Validators.required, Validators.pattern(PersonalConstants.regexValidators.newCityField)])],
            zipCode: ['', Validators.compose([Validators.required])],
            locationDescription: ['', Validators.compose([Validators.required])]
        });

        this.initializeStateOptions();
        this.setupCountryChangeListener();
        this.subscriber = this.vehicleLocationForm.statusChanges
            .subscribe(() => this.onVehicleLocationFormValid());

            this.searchTerm.pipe(
                switchMap((term) => {
                    const formCountry = this.vehicleLocationForm.get('country')?.value ?? 'US';
                    if (formCountry === 'US') {
                        return this.fetchSuggestions(term).pipe(
                            catchError((error) => {
                                this.logger.error('Error fetching suggestions:', error);
                                return of({ suggestions: [] });
                            })
                        );
                    }
                    return of({ suggestions: [] });
                })
            ).subscribe(({ suggestions }) => {
                this.addressLineSuggestions = suggestions.map((suggestion) => suggestion.text);
                this.addressKeys = suggestions.map((suggestion) => suggestion.pbkey);
            });
    }

    private initializeStateOptions(): void {
        const defaultCountry = this.vehicleLocationForm.get('country')?.value ?? 'US';
        this.updateStateOptions(defaultCountry);
    }

    fetchSuggestions(term: string): Observable<{ suggestions: { text: string; pbkey: string }[] }> {
        const formState = this.vehicleLocationForm.get('state')?.value;
        const formCountry = this.vehicleLocationForm.get('country')?.value ?? 'US';
        const country = formCountry === 'US' ? 'USA' : formCountry;

        return this.getSuggestions(term, country, formState).pipe(
            map((response) => this.filterAndParseSuggestions(response)),
            catchError((error) => {
                this.logger.error('Error fetching suggestions:', error);
                return of({ suggestions: [] });
            })
        );
    }

    getSuggestions(address: string, country: string, state?: string): Observable<any> {
        return this.locationService.getSuggestions(address, country, state);
    }

    filterAndParseSuggestions(response: any): { suggestions: { text: string; pbkey: string }[] } {
        const results: any[] = response?.response ?? [];
        this.suggestedParsedAddressess = [];
        const suggestions = results.map((address) => {
            const pbkey = (address?.pbkey ?? address?.PBKey ?? '').toString();
            const text = [address.addressLine, address.city, address.state, address.zip, 'USA']
                .filter(Boolean).join(', ');
            const parsed = {
                addressLine1: address.addressLine,
                city: address.city,
                state: address.state,
                zipCode: address.zip,
                country: 'US',
                pbkey
            };
            this.suggestedParsedAddressess.push(parsed);
            return { text, pbkey };
        });

        return { suggestions };
    }

    parseAddress(text: string): any {
        const [line1, city, state, zipCode, country] = text.split(',').map((s) => s.trim());
        return { addressLine1: line1, city, state, zipCode, country };
    }

    private updateStateOptions(country: string): void {
        const currentState = this.vehicleLocationForm.get('state')?.value;
        this.stateOptions = this.statesByCountry[country] ?? [];
        this.stateLabel = country === 'CA' ? 'Province' : 'State';
        this.zipCodeLabel = country === 'CA' ? 'Postal Code' : 'Zip Code';

        const addressLine1 = this.vehicleLocationForm.get('addressLine1')?.value;
        if (currentState && this.stateOptions.some((opt) => opt.value === currentState)) {
            this.vehicleLocationForm.get('state')?.setValue(currentState);
        } else if (addressLine1 && addressLine1.trim().toLowerCase() === this.UNKNOWN_ADDRESS_TEXT.toLowerCase()) {
            this.vehicleLocationForm.get('state')?.setValue('ZZ');
        } else {
            this.vehicleLocationForm.get('state')?.reset();
        }
    }

    formatCanadianPostalCode(event: Event): void {
        const zipCodeControl = this.vehicleLocationForm.get('zipCode');
        const country = this.vehicleLocationForm.get('country')?.value;
        if (country !== 'CA' || !zipCodeControl) return;

        const input = event.target as HTMLInputElement;
        let value = input.value.replace(/\s+/g, '').toUpperCase();
        if (value.length > 3 && value[3] !== ' ') {
            value = `${value.slice(0, 3)} ${value.slice(3, 6)}`;
            input.value = value;
            zipCodeControl.setValue(value, { emitEvent: false });
        } else if (input.value !== value) {
            input.value = value;
            zipCodeControl.setValue(value, { emitEvent: false });
        }
    }

    get formControls(): any {
        return this.vehicleLocationForm.controls;
    }

    vehicleLocationFormSubmit(): void {
        if (this.vehicleLocationForm.invalid) {
            return null;
        }
        return this.vehicleLocationForm.value;
    }

    triggerValidationForAllFields(): void {
        if (this.vehicleLocationForm.invalid) {
            this.vehicleLocationForm.markAllAsTouched();
            this.vehicleLocationForm.updateValueAndValidity();
        }
    }

    private onVehicleLocationFormValid(): void {
        this.formValid.emit(this.vehicleLocationForm);
    }

    private setupCountryChangeListener(): void {
        const zipCodeControl = this.vehicleLocationForm.get('zipCode');
        const countryControl = this.vehicleLocationForm.get('country');

        if (countryControl && zipCodeControl) {
            countryControl.valueChanges.subscribe((country) => {
                this.updateStateOptions(country);
                this.updateZipCodeValidators(country, zipCodeControl);
            });
            this.updateZipCodeValidators('US', zipCodeControl);
        }
    }

    updateZipCodeValidators(country: string, zipCodeControl: any): void {
        let zipCodeRegex: RegExp;

        switch (country) {
            case 'CA':
                zipCodeRegex = PersonalConstants.regexValidators.isCanadaZip;
                break;
            case 'MX':
                zipCodeRegex = PersonalConstants.regexValidators.isMexicoZip;
                break;
            default:
                zipCodeRegex = PersonalConstants.regexValidators.isUSorPRZip;
        }

        zipCodeControl.setValidators([Validators.pattern(zipCodeRegex)]);
        zipCodeControl.updateValueAndValidity();
    }

    setOptions(event: KeyboardEvent): void {
        const input = event.target as HTMLInputElement;
        const value = input.value;

        if (value.length % 3 === 0) {
            this.searchTerm.next(value);
        }

        (event.target as any).options = this.addressLineSuggestions;
        (event.target as any).optionvalues = this.addressKeys;
    }

    setAddressDetails(event: Event): void {
        const target = event.target as HTMLInputElement;
        const selectedValue = target?.value ?? '';

        const matchedAddress = this.suggestedParsedAddressess.find(
            (address) => address.pbkey === selectedValue
        ) ?? null;

        if (matchedAddress) {
            delete matchedAddress.pbkey;
            this.patchAddressForm(matchedAddress as vehicleLocationAddress);
            (event.target as any).displayvalue = matchedAddress.addressLine1;
        }
    }

    private patchAddressForm(address: vehicleLocationAddress): void {
        this.vehicleLocationForm.patchValue({
            addressLine1: address.addressLine1,
            addressLine2: address.addressLine2,
            city: address.city,
            state: address.state,
            country: address.country,
            zipCode: address.zipCode
        }, { emitEvent: true });
    }
}
